// PDF Template Types (shared between web and api apps)

import type { ConditionScope } from "../conditional-sources";
import type { LocalizedString } from "../i18n";
import type { AssetClassForecastHorizon } from "./asset-class-forecast";
import type { CloneJob } from "./clone-jobs";
import type { FlowFieldOption } from "./flows";
import type { ProductCostsSource } from "./product-costs";
import type { RiskSection } from "./risk-models";

// ============================================
// PDF BLOCK TYPES
// ============================================

export type PdfBlockType =
  | "text"
  | "heading"
  | "table"
  | "table_of_contents"
  | "financial_situation"
  | "image"
  | "advisor_image"
  | "spacer"
  | "page_break"
  | "two_column_layout"
  | "same_page"
  | "risk_assessment"
  | "portfolio_display"
  | "portfolio_xray"
  | "asset_allocation"
  | "asset_class_forecast"
  | "historical_return"
  | "cost_table"
  | "cost_chart"
  | "portfolio_costs"
  | "product_costs"
  | "client_classification"
  | "product_assessment"
  | "quiz_results"
  | "portfolio_forecast"
  | "expected_value"
  | "wealth_plan"
  | "sustainability_preferences"
  | "sustainability_status"
  | "client_preferences"
  | "goal_info"
  | "goal_list"
  | "group_members"
  | "client_parties"
  | "volatility_chart"
  | "equity_share_chart"
  | "mandate"
  | "mandate_comparison"
  | "mandate_portfolio"
  | "section_start";

export type PdfTextBlock = {
  type: "text";
  id: string;
  content: string;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    fontWeight?: "normal" | "bold";
    textAlign?: "left" | "center" | "right" | "justify";
    color?: string;
    marginTop?: number;
    marginBottom?: number;
    maxWidth?: number;
  };
};

/**
 * QL-414 — Heading components nest under the goal and portfolio they print
 * for, so numbering reads 6. → 6.1 → 6.4 → 6.4.1 and a table of contents
 * indents them:
 *
 * - global section: every heading is level 1 ("6.").
 * - per-goal section: the first heading printed for a goal is that goal's own
 *   entry, level 1 ("6. Goal: Pension 2035"); the rest are level 2 ("6.1").
 * - per-portfolio section: the first heading printed for a portfolio is that
 *   portfolio's entry, level 2 ("6.4 Portfolio: Pension core"); the rest are
 *   level 3 ("6.4.1").
 *
 * "First" is the first heading actually printed: one hidden by a conditional
 * display rule does not count.
 *
 * A portfolio only nests under its goal when the goal printed an entry. When
 * the goal's render printed no heading at all (no Heading component, or every
 * one hidden by its rule), `goalHasEntry` is false and the portfolio's
 * headings move up one level — its first is level 1, the rest level 2 — so
 * they never print as "0.1" or under an unrelated earlier heading.
 */
export type PdfHeadingLevel = 1 | 2 | 3;

export function pdfHeadingLevel(
  scope: PdfSectionScope,
  isFirstInSection: boolean,
  goalHasEntry = true,
): PdfHeadingLevel {
  if (scope === "per_goal" || (scope === "per_portfolio" && !goalHasEntry)) {
    return isFirstInSection ? 1 : 2;
  }
  if (scope === "per_portfolio") return isFirstInSection ? 2 : 3;
  return 1;
}

/**
 * QL-414 — a flow page with "Enable Advisor Notes" on, as the PDF editor's
 * Insert Field menu lists it. Each is one insertable field,
 * `{{AdvisorNote.<page id>}}` (shown as `{{AdvisorNote.<page name>}}`), which
 * prints the note written on that page: the instance's note for a global page,
 * the current goal's note for a goal page.
 */
export type PdfAdvisorNotePage = {
  id: string;
  name: string;
  nameI18n: Record<string, string> | null;
  /** Portfolio pages are never listed: the flow has no notes editor on them. */
  pageType: "global" | "goal";
};

/**
 * The name each page's field is shown under, unique within the list: the
 * page's name in the editor's language, and from the second page of the same
 * name on, that name with a number ("Summary (2)"). The number skips any name
 * already shown, so a page that is itself called "Summary (2)" never shares
 * it. A field is stored by page id but typed and read by name, so two fields
 * sharing a name could not be told apart, and a reference typed by name would
 * bind to the wrong page.
 */
export function advisorNotePageDisplayNames(
  pages: ReadonlyArray<{ id: string; name: string; nameI18n?: Record<string, string> | null }>,
  locale: string,
): Map<string, string> {
  const taken = new Set<string>();
  const names = new Map<string, string>();
  for (const page of pages) {
    const base = page.nameI18n?.[locale] || page.name;
    let name = base;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${base} (${n})`;
    taken.add(name.toLowerCase());
    names.set(page.id, name);
  }
  return names;
}

/** The placeholder namespace of the per-page advisor note fields. */
export const ADVISOR_NOTE_FIELD_PREFIX = "AdvisorNote.";

/** The field-value key the note written on one flow page travels under. */
export function advisorNoteFieldKey(flowPageId: string): string {
  return `advisor_note_${flowPageId}`;
}

export type PdfHeadingBlock = {
  type: "heading";
  id: string;
  title: string;
  /**
   * QL-414 — the heading's depth, from where it sits (see `pdfHeadingLevel`).
   * Absent = 1.
   */
  level?: PdfHeadingLevel;
  fieldValues?: Record<string, string>;
  style?: {
    textAlign?: "left" | "center" | "right";
    color?: string;
    marginTop?: number;
    marginBottom?: number;
  };
};

export type PdfTableColumn = {
  header: string;
  fieldId?: string;
  staticValue?: string;
  width?: number;
  align?: "left" | "center" | "right";
};

export type PdfTableBlock = {
  type: "table";
  id: string;
  title?: string;
  description?: string;
  rows: Array<{
    col1: string;
    col2: string;
  }>;
  /** QL-384 — first row drawn as a header, repeated after a page break. */
  headerRow?: boolean;
  /** QL-384 — every cell in column 1 drawn as a header. */
  headerColumn?: boolean;
  /** QL-384 — label / value column proportions. */
  columnWidth?: PdfTableColumnWidth;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    marginTop?: number;
    marginBottom?: number;
  };
};

/**
 * QL-384 — lists the Heading components in document order with their page.
 * Component titles, headings hidden by a condition and attached PDFs are not
 * listed; a heading in a per-goal or per-portfolio section lists once per goal
 * or portfolio.
 */
export type PdfTableOfContentsBlock = {
  type: "table_of_contents";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/** QL-384 — the parts of the Financial situation component a template prints. */
export const PDF_FINANCIAL_SITUATION_PARTS = [
  "income",
  "assets",
  "liabilities",
  "commitments",
  "capacity",
  "summary",
  "incomeStability",
  "expectingChanges",
] as const;
export type PdfFinancialSituationPart = (typeof PDF_FINANCIAL_SITUATION_PARTS)[number];

/**
 * QL-384 — the client's financial situation as captured in the flow, prepared
 * for printing: field labels resolved in the template language, amounts in
 * the flow currency. Built by the PDF field builder from the stored
 * financial_situation value (input ∪ calculated). A part with nothing
 * captured is absent or empty.
 */
export type PdfFinancialSituationSnapshot = {
  currency: string;
  income: { rows: PdfFinancialSituationRow[]; total: number };
  /** Entered assets, then the client's synced accounts (they are in the total). */
  assets: { rows: PdfFinancialSituationRow[]; total: number };
  liabilities: {
    rows: Array<PdfFinancialSituationRow & { monthlyPayment: number | null }>;
    total: number;
    totalMonthlyPayment: number;
  };
  commitments: { rows: PdfFinancialSituationRow[]; total: number };
  /**
   * Present only when the model's investment capacity calculator was on and
   * the advisor entered at least one of its inputs; derived values alone
   * (the buffer minimum, a 0 short-term figure) do not count.
   */
  capacity: {
    monthlyIncome: number | null;
    monthlyExpenses: number | null;
    monthlySurplus: number | null;
    liquidityBuffer: number;
    shortTermExpenses: number;
    availableForInvestments: number;
  } | null;
  /** null when nothing was entered. */
  summary: {
    netWorth: number;
    /**
     * The household ratios, as fractions (0.39 = 39 %). null for a Company
     * client, as in the widget (`fsShowsHouseholdRatios`, QP-176).
     */
    ratios: {
      debtToIncomeRatio: number;
      monthlyDebtServiceRatio: number;
      savingsRate: number;
    } | null;
  } | null;
  incomeStability: "permanent" | "temporary" | "variable" | null;
  /** null when the question was not answered. */
  expectingChanges: { answer: boolean; note: string } | null;
};

export type PdfFinancialSituationRow = {
  /** The field's label in the template language (a synced account: its vendor). */
  type: string;
  description: string;
  /**
   * null for a synced account that could not be converted (not in the total),
   * and for a liability with only a monthly payment entered.
   */
  amount: number | null;
  /** A synced client account rather than an entered asset. */
  account?: { originalAmount: number | null; originalCurrency: string; converted: boolean };
};

/**
 * QL-414 — how a PDF block that prints money shows its currency.
 *
 * - `with_amounts`: every amount carries the code ("60 000 EUR").
 * - `note`: bare amounts, and one "All amounts in EUR" line right-aligned
 *   under the block's last table or chart.
 * - `off`: no currency anywhere.
 *
 * A chart never carries the currency on its axis, so `with_amounts` adds
 * nothing to one; a block that only draws a chart offers `note` / `off`.
 */
export const PDF_CURRENCY_DISPLAYS = ["with_amounts", "note", "off"] as const;
export type PdfCurrencyDisplay = (typeof PDF_CURRENCY_DISPLAYS)[number];

/**
 * Every block with the setting, and what it prints when the setting is absent:
 * what it printed before the setting existed, so a saved template is unchanged
 * until a designer picks an option. (The financial situation block's line moved
 * from above its tables to below them.)
 *
 * Goal info is not listed: it has printed no amount since QP-150 moved amounts
 * to the portfolio, so the setting would change nothing there.
 */
export const PDF_CURRENCY_DISPLAY_DEFAULTS = {
  financial_situation: "note",
  portfolio_display: "with_amounts",
  portfolio_forecast: "with_amounts",
  expected_value: "with_amounts",
  cost_table: "with_amounts",
  portfolio_costs: "with_amounts",
  wealth_plan: "with_amounts",
  cost_chart: "off",
  // QL-730 B2 — the basis table / the checked value.
  mandate: "with_amounts",
  mandate_portfolio: "with_amounts",
} as const satisfies Record<string, PdfCurrencyDisplay>;

export type PdfCurrencyBlockType = keyof typeof PDF_CURRENCY_DISPLAY_DEFAULTS;

export function isPdfCurrencyBlockType(type: string): type is PdfCurrencyBlockType {
  return Object.hasOwn(PDF_CURRENCY_DISPLAY_DEFAULTS, type);
}

/**
 * Blocks that only ever draw a chart: no amount to carry the code, so they
 * offer `note` / `off`. Add a new chart-only money block here as well as to
 * the defaults above.
 */
const PDF_CURRENCY_CHART_ONLY_BLOCKS: ReadonlySet<PdfCurrencyBlockType> = new Set(["cost_chart"]);

const PDF_CURRENCY_CHART_ONLY_DISPLAYS = ["note", "off"] as const;

/** The options a block offers: a chart-only block has no amounts to carry the code. */
export function pdfCurrencyDisplayOptions(
  type: PdfCurrencyBlockType,
): readonly PdfCurrencyDisplay[] {
  return PDF_CURRENCY_CHART_ONLY_BLOCKS.has(type)
    ? PDF_CURRENCY_CHART_ONLY_DISPLAYS
    : PDF_CURRENCY_DISPLAYS;
}

/** The stored setting, or the block's default when it is absent or not one the block offers. */
export function resolvePdfCurrencyDisplay(
  type: PdfCurrencyBlockType,
  value: unknown,
): PdfCurrencyDisplay {
  return (pdfCurrencyDisplayOptions(type) as readonly unknown[]).includes(value)
    ? (value as PdfCurrencyDisplay)
    : PDF_CURRENCY_DISPLAY_DEFAULTS[type];
}

/**
 * QL-384 — prints the client's financial situation (income, assets,
 * liabilities, commitments, capacity, summary, income stability, expecting
 * changes). Reads `financial_situation_snapshot` (a PdfFinancialSituationSnapshot)
 * from the field values; prints nothing when the flow has none.
 */
export type PdfFinancialSituationBlock = {
  type: "financial_situation";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  /** Parts switched off in the config; absent = printed. */
  hiddenParts?: PdfFinancialSituationPart[];
  fieldValues?: Record<string, string>;
};

export type PdfImageBlock = {
  type: "image";
  id: string;
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  align?: "left" | "center" | "right";
  marginTop?: number;
  marginBottom?: number;
};

export type PdfAdvisorImageBlock = {
  type: "advisor_image";
  id: string;
  width?: number;
  align?: "left" | "center" | "right";
  shape?: "square" | "circle";
  fieldValues?: Record<string, string>;
};

export type PdfSpacerBlock = {
  type: "spacer";
  id: string;
  height: number;
};

export type PdfPageBreakBlock = {
  type: "page_break";
  id: string;
};

export type PdfSectionStartBlock = {
  type: "section_start";
  id: string;
  sectionName: string;
  isFirstSection: boolean;
  bgColor?: string | null;
  bgImage?: string | null;
  bgImageType?: "png" | "jpg" | "pdf" | null;
  headerText?: string | null;
  headerTextAlign?: "left" | "center" | "right" | null;
  headerLogo?: string | null;
  headerLogoType?: "svg" | "png" | "jpg" | null;
  headerLogoAlign?: "left" | "center" | "right" | null;
  headerShowLine?: boolean;
  headerLineColor?: string | null;
  footerShowNumbers?: boolean;
  footerPageNumberAlign?: PdfPageNumberAlign;
  footerShowLine?: boolean;
  footerLineColor?: string | null;
  /** QL-384 — every page of the section is landscape when set. */
  orientation?: PdfPageOrientation;
  fieldValues?: Record<string, string>;
};

export type PdfTwoColumnLayoutBlock = {
  type: "two_column_layout";
  id: string;
  leftColumn: PdfBlock[];
  rightColumn: PdfBlock[];
  columnGap?: number;
};

/**
 * QL-384 — a Same Page Block: its components render in order, full width,
 * and move to the next page as one piece instead of splitting. A block taller
 * than a full page still splits.
 */
export type PdfSamePageBlock = {
  type: "same_page";
  id: string;
  children: PdfBlock[];
};

export type PdfRiskAssessmentBlock = {
  type: "risk_assessment";
  id: string;
  title?: string;
  description?: string;
  /**
   * QL-9 — render the score derivation (initial score, adjustment, optional
   * cap) under the Risk Level row. Strict opt-in: only an explicit `true`
   * enables it, so configs persisted before the flag existed stay off.
   */
  showAdjustments?: boolean;
  /**
   * QL-212 — which of the assessment's four sections this placement prints,
   * as decided by `renderedRiskSectionsInPdfSection` for the section the
   * component sits in. Absent ⇒ all four (a block built without a risk model
   * in context, or persisted before the field existed).
   */
  sections?: RiskSection[];
  style?: {
    fontSize?: number;
    fontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
  fieldValues?: Record<string, string>;
};

export type PdfPortfolioDisplayBlock = {
  type: "portfolio_display";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  showAsTable?: boolean;
  showWeights?: boolean;
  showIsin?: boolean;
  showAmount?: boolean;
  showUnits?: boolean;
  style?: {
    fontSize?: number;
    headerFontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
  fieldValues?: Record<string, string>;
};

/** Which slice of the X-ray snapshot a portfolio_xray component renders. */
export type PdfXrayDisplayMode =
  | "asset_allocation"
  | "country_exposure"
  | "regional_exposure"
  | "stock_sector"
  | "bond_sector"
  | "equity_style"
  | "top_holdings";

export type PdfPortfolioXrayBlock = {
  type: "portfolio_xray";
  id: string;
  title?: string;
  description?: string;
  displayMode?: PdfXrayDisplayMode;
  showAsChart?: boolean;
  showAsTable?: boolean;
  showPercentages?: boolean;
  /**
   * QP-177 — render existing-vs-modified comparison when the portfolio is a
   * followup (an `existing_xray_snapshot` field value is present). Strict
   * opt-in: only an explicit `true` enables it, so configs persisted before
   * the flag existed stay off. Not supported for top_holdings.
   */
  showFollowupComparison?: boolean;
  /** Agreement follow-up: comparison headers say "Savings Plan". */
  isSavingsPlanFollowup?: boolean;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    headerFontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
};

export type PdfAssetAllocationBlock = {
  type: "asset_allocation";
  id: string;
  title?: string;
  description?: string;
  showAsChart?: boolean;
  showAsTable?: boolean;
  /** QP-177 — same strict opt-in comparison flag as PdfPortfolioXrayBlock. */
  showFollowupComparison?: boolean;
  /** Agreement follow-up: comparison headers say "Savings Plan". */
  isSavingsPlanFollowup?: boolean;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    headerFontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
};

/**
 * QL-315 — the asset-class mix over time, as a 100%-normalised stacked bar.
 *
 * The PDF has no horizon toggle, so it prints one range, `timeRange` (QL-414;
 * absent = the full ten-year grid). The snapshot stores every point; the range
 * is only a window over it.
 * `fieldValues` carries the snapshot: the goal-scoped row under a per-goal
 * section, the instance-wide row under a global one.
 */
export type PdfAssetClassForecastBlock = {
  type: "asset_class_forecast";
  id: string;
  title?: string;
  description?: string;
  timeRange?: AssetClassForecastHorizon;
  showChart?: boolean;
  showTable?: boolean;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    headerFontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
};

export type PdfPortfolioForecastBlock = {
  type: "portfolio_forecast";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  showChart?: boolean;
  showTable?: boolean;
  /** Draw the bank-savings comparison line from the snapshot's bank series. Default false. */
  compareWithBank?: boolean;
  fieldValues?: Record<string, string>;
  style?: {
    fontSize?: number;
    headerFontWeight?: "normal" | "bold";
    marginTop?: number;
    marginBottom?: number;
  };
};

export type PdfClientClassificationBlock = {
  type: "client_classification";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfProductAssessmentBlock = {
  type: "product_assessment";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QL-505 — the standalone knowledge quiz's own PDF block (`quiz` flow
 * component). Global scope, same as `product_assessment` — the quiz is
 * instance-wide, not tied to a goal or portfolio.
 */
export type PdfQuizResultsBlock = {
  type: "quiz_results";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfCostTableBlock = {
  type: "cost_table";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  // QP-208 — optional second "After N years" column (MiFID II holding-period slice)
  showHoldingPeriod?: boolean; // Default: false
  holdingPeriodYears?: number; // Default: 10; capped at the simulated series length
  fieldValues?: Record<string, string>;
};

export type PdfHistoricalReturnBlock = {
  type: "historical_return";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfCostChartBlock = {
  type: "cost_chart";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  displayMode?: "rolling" | "calendar";
  /** Line (two lines, before/after cost) or stacked bar (net + cost drag). Default "line". */
  chartType?: "line" | "bar";
  /** QP-208 — how many years of the simulated series to display. Default 10. */
  projectionYears?: number;
  fieldValues?: Record<string, string>;
};

export type PdfExpectedValueBlock = {
  type: "expected_value";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  showAsChart?: boolean;
  showAsSummaryTable?: boolean;
  /** Draw the bank-savings comparison line from the snapshot's bank series. Default false. */
  compareWithBank?: boolean;
  fieldValues?: Record<string, string>;
};

export type PdfWealthPlanBlock = {
  type: "wealth_plan";
  id: string;
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  fieldValues?: Record<string, string>;
};

/**
 * QP-179 — the client's stated sustainability preferences (global scope).
 * Reads `sustainability_preferences_snapshot` from fieldValues.
 */
export type PdfSustainabilityPreferencesBlock = {
  type: "sustainability_preferences";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QP-224 — the client's preference answers (global scope).
 * Reads `client_preferences_snapshot` from fieldValues.
 */
export type PdfClientPreferencesBlock = {
  type: "client_preferences";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QP-179 — the per-goal sustainability matching table + decision (per_goal
 * scope). Reads `sustainability_status_snapshot` from the goal fieldValues.
 */
export type PdfSustainabilityStatusBlock = {
  type: "sustainability_status";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QL-314 — volatility chart with compliance gate (per_goal scope).
 *
 * QL-414 — `timeRange` picks the one range printed (absent = 10Y);
 * `showChart` / `showTable` are opt-out (absent = shown). The summary line and
 * the decision callout print whatever the two are set to.
 */
export type PdfVolatilityChartBlock = {
  type: "volatility_chart";
  id: string;
  title?: string;
  description?: string;
  timeRange?: AssetClassForecastHorizon;
  showChart?: boolean;
  showTable?: boolean;
  fieldValues?: Record<string, string>;
};

/**
 * QL-313 — equity share stacked bar with compliance gate (per_goal scope).
 * QL-414 — `timeRange`, `showChart`, `showTable` as on the volatility block.
 */
export type PdfEquityShareChartBlock = {
  type: "equity_share_chart";
  id: string;
  title?: string;
  description?: string;
  timeRange?: AssetClassForecastHorizon;
  showChart?: boolean;
  showTable?: boolean;
  fieldValues?: Record<string, string>;
};

/** QL-375 group 10 — the mandate that applied, the goal's weights against it, the accepted deviation (screen 13). */
export type PdfMandateBlock = {
  type: "mandate";
  id: string;
  title?: string;
  description?: string;
  /** QL-730 B2 — the basis table prints money (the projected year-1 values). */
  currencyDisplay?: PdfCurrencyDisplay;
  fieldValues?: Record<string, string>;
};

/**
 * QL-730 B2 — one portfolio's own mandate check, for a per-portfolio section:
 * the value it was checked on, its lines against the mandate, its verdict and
 * — under portfolio-level blocking — its decision. Reads the goal's frozen
 * snapshot and the section's `portfolio_id`.
 */
export type PdfMandatePortfolioBlock = {
  type: "mandate_portfolio";
  id: string;
  title?: string;
  description?: string;
  currencyDisplay?: PdfCurrencyDisplay;
  fieldValues?: Record<string, string>;
};

/** QL-375 group 10 — the bank's existing mandate beside the proposed one (screen 13). */
export type PdfMandateComparisonBlock = {
  type: "mandate_comparison";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfGoalInfoBlock = {
  type: "goal_info";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QL-281 — instance-wide goal list (global scope). Reads `goal_list_snapshot`
 * from fieldValues: every goal of the instance with its localized goal-type and
 * platform labels.
 */
export type PdfGoalListBlock = {
  type: "goal_list";
  id: string;
  title?: string;
  description?: string;
  /** Print the goals' descriptions as a column (only when at least one goal has one). Default off. */
  showDescription?: boolean;
  fieldValues?: Record<string, string>;
};

/** One row of the `goal_list_snapshot` payload the goal_list block renders. */
export type PdfGoalListSnapshotGoal = {
  id: string;
  name: string;
  description: string | null;
  goalTypeLabel: string | null;
  platformLabel: string | null;
};

export type PdfPortfolioCostsBlock = {
  type: "portfolio_costs";
  id: string;
  title?: string;
  /** QL-414 — absent = the block's entry in PDF_CURRENCY_DISPLAY_DEFAULTS. */
  currencyDisplay?: PdfCurrencyDisplay;
  fieldValues?: Record<string, string>;
};

export type PdfProductCostsBlock = {
  type: "product_costs";
  id: string;
  title?: string;
  /** QL-417 — "existing" renders `existing_product_costs_snapshot` when present. */
  portfolioSource?: ProductCostsSource;
  fieldValues?: Record<string, string>;
};

export type PdfTrapetsLookupBlock = {
  type: "trapets_lookup";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfSumsubVerificationBlock = {
  type: "sumsub_verification";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfEninLookupBlock = {
  type: "enin_lookup";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QP-184 — group composition table (Group clients). Reads
 * `group_members_snapshot` from fieldValues; renders nothing for other types.
 */
export type PdfGroupMembersBlock = {
  type: "group_members";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

/**
 * QP-184 — related parties table (representatives / POA / guardians).
 * Reads `client_parties_snapshot` from fieldValues.
 */
export type PdfClientPartiesBlock = {
  type: "client_parties";
  id: string;
  title?: string;
  description?: string;
  fieldValues?: Record<string, string>;
};

export type PdfBlock =
  | PdfTextBlock
  | PdfHeadingBlock
  | PdfTableBlock
  | PdfTableOfContentsBlock
  | PdfFinancialSituationBlock
  | PdfImageBlock
  | PdfAdvisorImageBlock
  | PdfSpacerBlock
  | PdfPageBreakBlock
  | PdfSectionStartBlock
  | PdfTwoColumnLayoutBlock
  | PdfSamePageBlock
  | PdfRiskAssessmentBlock
  | PdfPortfolioDisplayBlock
  | PdfPortfolioXrayBlock
  | PdfAssetAllocationBlock
  | PdfAssetClassForecastBlock
  | PdfCostTableBlock
  | PdfHistoricalReturnBlock
  | PdfCostChartBlock
  | PdfPortfolioCostsBlock
  | PdfProductCostsBlock
  | PdfClientClassificationBlock
  | PdfProductAssessmentBlock
  | PdfQuizResultsBlock
  | PdfPortfolioForecastBlock
  | PdfExpectedValueBlock
  | PdfWealthPlanBlock
  | PdfSustainabilityPreferencesBlock
  | PdfSustainabilityStatusBlock
  | PdfVolatilityChartBlock
  | PdfEquityShareChartBlock
  | PdfMandateBlock
  | PdfMandateComparisonBlock
  | PdfMandatePortfolioBlock
  | PdfClientPreferencesBlock
  | PdfGoalInfoBlock
  | PdfGoalListBlock
  | PdfTrapetsLookupBlock
  | PdfSumsubVerificationBlock
  | PdfEninLookupBlock
  | PdfGroupMembersBlock
  | PdfClientPartiesBlock;

export type PdfTemplateStructure = {
  blocks: PdfBlock[];
  settings?: {
    pageSize?: "A4" | "Letter";
    orientation?: "portrait" | "landscape";
    margins?: {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };
    header?: {
      enabled: boolean;
      content?: string;
    };
    footer?: {
      enabled: boolean;
      content?: string;
      showPageNumbers?: boolean;
    };
  };
};

// ============================================
// FONT & STYLE CONSTANTS
// ============================================

export const PDF_FONT_CATEGORIES = {
  "Sans Serif": [
    "IBM Plex Sans",
    "Inter",
    "Inter Display",
    "Libre Franklin",
    "Manrope",
    "Nunito Sans",
    "Outfit",
    "Plus Jakarta Sans",
    "Roboto",
    "Rubik",
    "Source Sans 3",
    "Work Sans",
  ],
  Serif: [
    "Alegreya",
    "Cormorant Garamond",
    "Crimson Pro",
    "EB Garamond",
    "IBM Plex Serif",
    "Literata",
    "Lora",
    "Neuton",
    "Source Serif 4",
    "Spectral",
  ],
  Monospace: ["Fira Code", "IBM Plex Mono", "JetBrains Mono", "Source Code Pro"],
  Default: ["Söhne"],
} as const;

export const PDF_FONT_FAMILIES = Object.values(PDF_FONT_CATEGORIES).flat();

export type PdfFontCategory = keyof typeof PDF_FONT_CATEGORIES;
export type PdfFontFamily = (typeof PDF_FONT_CATEGORIES)[PdfFontCategory][number];

export const PDF_DEFAULT_FONT: PdfFontFamily = "Söhne";

export const PDF_FONT_SIZES = [8, 9, 10, 11, 12, 13, 14] as const;
export type PdfFontSize = (typeof PDF_FONT_SIZES)[number];

/**
 * QL-384 — the upright weights (OS/2 usWeightClass) each bundled PDF font
 * ships, read from the files in apps/api/fonts. Typst picks the closest weight
 * without warning when asked for one a font lacks, so the heading weight is
 * offered and stored only from this list.
 * `apps/api/src/tests/pdf-heading-style.test.ts` re-reads the font files and
 * fails when this map drifts from them.
 */
export const PDF_FONT_WEIGHTS: Record<PdfFontFamily, readonly number[]> = {
  "IBM Plex Sans": [200, 300, 400, 450, 500, 600, 700],
  Inter: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  "Inter Display": [100, 200, 300, 400, 500, 600, 700, 800, 900],
  "Libre Franklin": [100, 200, 300, 400, 500, 600, 700, 800, 900],
  Manrope: [250, 300, 400, 500, 600, 700, 800],
  "Nunito Sans": [200, 300, 400, 500, 600, 700, 800, 900],
  Outfit: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  "Plus Jakarta Sans": [200, 300, 400, 500, 600, 700, 800],
  Roboto: [250, 300, 400, 500, 600, 700, 800, 900],
  Rubik: [300, 400, 500, 700, 900],
  "Source Sans 3": [200, 300, 400, 500, 600, 700, 900],
  "Work Sans": [100, 200, 260, 280, 300, 400, 500, 600, 700, 800, 900],
  Alegreya: [400, 500, 700, 800, 900],
  "Cormorant Garamond": [300, 400, 500, 600, 700],
  "Crimson Pro": [200, 300, 400, 500, 600, 700, 800, 900],
  "EB Garamond": [400, 500, 600, 700, 800],
  "IBM Plex Serif": [100, 200, 300, 400, 450, 500, 600, 700],
  Literata: [200, 300, 400, 500, 600, 700, 800, 900],
  Lora: [400, 500, 600, 700],
  Neuton: [275, 300, 400, 700, 800],
  "Source Serif 4": [200, 300, 400, 600, 700, 900],
  Spectral: [250, 275, 300, 400, 500, 600, 700, 800],
  "Fira Code": [300, 400, 450, 500, 600, 700],
  "IBM Plex Mono": [100, 200, 300, 400, 450, 500, 600, 700],
  "JetBrains Mono": [100, 200, 300, 400, 500, 600, 700, 800],
  "Source Code Pro": [200, 300, 400, 500, 600, 700, 900],
  Söhne: [200, 300, 400, 500, 600, 650, 700, 800],
};

/** Weights a family ships; unknown families fall back to the default font's. */
export function pdfFontWeights(family: string | null | undefined): readonly number[] {
  return PDF_FONT_WEIGHTS[family as PdfFontFamily] ?? PDF_FONT_WEIGHTS[PDF_DEFAULT_FONT];
}

/** The weight the family has that is closest to `weight`; ties go to the heavier one. */
export function nearestPdfFontWeight(family: string | null | undefined, weight: number): number {
  let best = pdfFontWeights(family)[0] ?? weight;
  for (const candidate of pdfFontWeights(family)) {
    const d = Math.abs(candidate - weight);
    const bestD = Math.abs(best - weight);
    if (d < bestD || (d === bestD && candidate > best)) best = candidate;
  }
  return best;
}

const PDF_FONT_WEIGHT_NAMES: Record<number, string> = {
  100: "Thin",
  200: "Extralight",
  300: "Light",
  400: "Regular",
  450: "Text",
  500: "Medium",
  600: "Semibold",
  650: "Demibold",
  700: "Bold",
  800: "Extrabold",
  900: "Black",
};

/**
 * A readable name for a weight. In-between weights some fonts ship (250, 275
 * …) are named after the nearest standard weight, with the number.
 */
export function pdfFontWeightLabel(weight: number): string {
  const name = PDF_FONT_WEIGHT_NAMES[weight];
  if (name) return name;
  const nearest = Math.round(weight / 100) * 100;
  return `${PDF_FONT_WEIGHT_NAMES[nearest] ?? "Weight"} (${weight})`;
}

/** QL-384 — heading sizes offered on the Style tab, in points. */
export const PDF_HEADING_FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28] as const;

export const TABLE_STYLE_TYPES = ["borders", "alternating"] as const;
export type TableStyleType = (typeof TABLE_STYLE_TYPES)[number];

/** PATCH /internal/pdf-templates/:id/style — body. */
export type UpdatePdfTemplateStyleInput = {
  fontFamily?: string | null;
  fontSize?: number | null;
  tableHeaderBgColor?: string | null;
  tableHeaderTextColor?: string | null;
  tableStyleType?: string | null;
  tableAlternatingColor?: string | null;
  headingFontSize?: number | null;
  headingFontWeight?: number | null;
  headingColor?: string | null;
  headingNumbered?: boolean;
};

/** PATCH /internal/pdf-templates/:id/style — response. */
export type UpdatePdfTemplateStyleResponse = {
  success: true;
  /**
   * Set when the font in use lacks the heading weight: the weight was moved
   * to the nearest one the font has.
   */
  headingWeightAdjusted?: { from: number; to: number };
};

/** QL-384 — the Table component's column proportions (label / value). */
export const PDF_TABLE_COLUMN_WIDTHS = ["50/50", "30/70", "70/30"] as const;
export type PdfTableColumnWidth = (typeof PDF_TABLE_COLUMN_WIDTHS)[number];
export const PDF_DEFAULT_TABLE_COLUMN_WIDTH: PdfTableColumnWidth = "50/50";

/** QL-384 — a section's page orientation. */
export const PDF_PAGE_ORIENTATIONS = ["portrait", "landscape"] as const;
export type PdfPageOrientation = (typeof PDF_PAGE_ORIENTATIONS)[number];
export const PDF_DEFAULT_PAGE_ORIENTATION: PdfPageOrientation = "portrait";

/** QL-384 — where a section footer places the page number. */
export const PDF_PAGE_NUMBER_ALIGNS = ["left", "center", "right"] as const;
export type PdfPageNumberAlign = (typeof PDF_PAGE_NUMBER_ALIGNS)[number];
export const PDF_DEFAULT_PAGE_NUMBER_ALIGN: PdfPageNumberAlign = "center";

export type TableStyle = {
  headerBgColor?: string | null;
  headerTextColor?: string | null;
  styleType?: TableStyleType | null;
  alternatingColor?: string | null;
};

// ============================================
// DATABASE MODELS
// ============================================

/**
 * QL-334 — POST /internal/pdf-templates/:id/clone. A flow has at most one PDF
 * template, so the copy goes onto another flow that has none; it takes that
 * flow's language.
 */
export type ClonePdfTemplateRequest = {
  name: string;
  description?: string;
  /** The flow the copy belongs to. Must not have a PDF template yet. */
  flowId: string;
  /**
   * When the target flow's language differs from the source template's,
   * AI-translate the copy's texts into it. Ignored for the same language.
   */
  translate?: boolean;
};

/**
 * QL-334 — a plain copy is made at once ("copied", 201); a translated one is
 * queued ("queued", 202) and the job reports its progress.
 */
export type ClonePdfTemplateResponse =
  | { kind: "copied"; id: string }
  | { kind: "queued"; job: CloneJob };

export type PdfTemplate = {
  id: string;
  name: string;
  description: string | null;
  flowId: string;
  isActive: boolean;
  includeProductDocuments: boolean;
  fontFamily: string | null;
  fontSize: number | null;
  tableHeaderBgColor: string | null;
  tableHeaderTextColor: string | null;
  tableStyleType: string | null;
  tableAlternatingColor: string | null;
  // QL-384 — template-wide heading style; null = today's rendering.
  headingFontSize: number | null;
  headingFontWeight: number | null;
  headingColor: string | null;
  headingNumbered: boolean;
  language: string;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type PdfTemplateWithFlow = PdfTemplate & {
  flow: {
    id: string;
    name: string;
  };
};

/**
 * A document that could not be included in the generated PDF — a product
 * document (never attached, or the fetch/merge failed) or a template
 * attachment whose fetch/merge failed. For attachments `productName` holds
 * the attachment file name and `isin` is null.
 */
export type PdfMissingDocument = {
  productName: string;
  isin: string | null;
  documentTypeNameI18n: LocalizedString;
  reason: "no_document" | "fetch_failed" | "invalid_pdf";
  source?: "product" | "attachment";
};

export type PdfGenerationLog = {
  id: string;
  templateId: string;
  flowInstanceId: string;
  s3Key: string;
  s3Url: string | null;
  fileSize: number | null;
  status: string;
  errorMessage: string | null;
  missingDocuments: PdfMissingDocument[] | null;
  generatedAt: string | null;
  downloadCount: number;
  lastDownloadAt: Date | null;
  generatedById: string;
  createdAt: Date;
  updatedAt: Date;
};

export type PdfGenerationLogWithDetails = PdfGenerationLog & {
  template: {
    id: string;
    name: string;
  };
  flowInstance: {
    id: string;
    flow: {
      id: string;
      name: string;
    };
    client: {
      id: string;
      name: string;
    };
  };
};

export type CreatePdfTemplateInput = {
  name: string;
  description?: string;
  flowId: string;
  language?: string;
  template: PdfTemplateStructure;
  isActive?: boolean;
};

export type UpdatePdfTemplateInput = {
  name?: string;
  description?: string;
  template?: PdfTemplateStructure;
  isActive?: boolean;
};

export type GeneratePdfInput = {
  templateId: string;
  flowInstanceId: string;
};

export type PdfTemplateFilters = {
  limit?: number;
  offset?: number;
  flowId?: string;
  isActive?: boolean;
};

export type PdfGenerationLogFilters = {
  limit?: number;
  offset?: number;
  templateId?: string;
  flowInstanceId?: string;
  status?: string;
};

// ============================================
// TEMPLATE ATTACHMENTS (QP-220)
// ============================================

export type PdfConditionalOperator =
  | "equals"
  | "not_equals"
  | "greater_than"
  | "less_than"
  | "is_empty"
  | "is_not_empty"
  | "contains"
  | "not_contains";

/**
 * QP-220 — conditional inclusion rule for a template attachment. Evaluated
 * against the instance-wide field values (global scope only — attachments are
 * appended once per PDF, so goal/portfolio-scoped sources don't apply).
 */
export type PdfAttachmentConditionalDisplay = {
  enabled: boolean;
  sourceFieldId: string;
  operator: PdfConditionalOperator;
  value?: string;
};

/**
 * QP-220 — a static PDF uploaded on the template. Appended (pdf-lib merge) to
 * the end of the generated report body in `order`, before product documents.
 */
export type PdfTemplateAttachment = {
  id: string;
  pdfTemplateId: string;
  fileName: string;
  s3Key: string;
  fileSize: number;
  pageCount: number;
  order: number;
  conditionalDisplay: PdfAttachmentConditionalDisplay | null;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type UpdatePdfTemplateAttachmentInput = {
  conditionalDisplay?: PdfAttachmentConditionalDisplay | null;
};

export type ReorderPdfTemplateAttachmentsInput = {
  attachmentIds: string[];
};

// ============================================
// SECTION-BASED PDF ARCHITECTURE (v3)
// ============================================

export type PdfSectionScope = "global" | "per_goal" | "per_portfolio";

export type PdfSection = {
  id: string;
  pdfTemplateId: string;
  name: string;
  nameI18n?: Record<string, string> | null;
  order: number;
  scope: string;
  // QP-150 — set on a per_portfolio sub-node, pointing at its owning per_goal section; null otherwise.
  parentSectionId: string | null;
  orientation: string;
  bgColor: string | null;
  bgImage: string | null;
  bgImageType: string | null;
  headerText: string | null;
  headerTextI18n?: Record<string, string> | null;
  headerTextAlign: string | null;
  headerLogo: string | null;
  headerLogoType: string | null;
  headerLogoAlign: string | null;
  headerShowLine: boolean;
  headerLineColor: string | null;
  footerShowNumbers: boolean;
  footerPageNumberAlign: string;
  footerShowLine: boolean;
  footerLineColor: string | null;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type PdfSectionWithPages = PdfSection & {
  pages: (PdfTemplatePage & { components?: PdfPageComponent[] })[];
};

export type PdfTemplatePage = {
  id: string;
  pdfSectionId: string | null;
  pdfTemplateId: string | null;
  name: string;
  order: number;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type PdfTemplatePageWithComponents = PdfTemplatePage & {
  layouts: PdfPageLayoutWithComponents[];
  components: PdfPageComponent[];
};

/**
 * QL-384 — a page layout row is a 2-Column Block or a Same Page Block. Its
 * components sit in `left` / `right` (two_column) or `main` (same_page).
 */
export const PDF_LAYOUT_TYPES = ["two_column", "same_page"] as const;
export type PdfLayoutType = (typeof PDF_LAYOUT_TYPES)[number];
/** A block column: `left` / `right` in a 2-Column Block, `main` in a Same Page Block. */
export type PdfLayoutColumn = "left" | "right" | "main";
export const PDF_LAYOUT_COLUMNS: Record<PdfLayoutType, readonly PdfLayoutColumn[]> = {
  two_column: ["left", "right"],
  same_page: ["main"],
};

export type PdfPageLayout = {
  id: string;
  pdfTemplatePageId: string;
  order: number;
  layoutType: PdfLayoutType;
  config: string | null;
  tenantId: string;
  createdAt: string;
};

/** POST /internal/pdf-templates/pages/:pageId/layouts — body. */
export type CreatePdfLayoutInput = {
  order?: number;
  /** Missing or null → two_column. */
  layoutType?: PdfLayoutType | null;
};

export type PdfPageLayoutWithComponents = PdfPageLayout & {
  components: PdfPageComponent[];
};

export type PdfPageComponent = {
  id: string;
  pdfTemplatePageId: string;
  pdfPageLayoutId: string | null;
  columnPosition: string | null;
  componentType: string;
  config: string | null;
  configI18n?: Record<string, string> | null;
  order: number;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

// ============================================
// COMPONENT SCOPE CONSTANTS & VALIDATION
// ============================================

// Goal-level components — identity/suitability that stays on the goal (QP-150).
export const GOAL_SCOPED_COMPONENTS = [
  "goal_name",
  "goal_info",
  // QP-179 — matching table aggregates the goal's portfolios; goal-scoped.
  "sustainability_status",
  // QL-314 — volatility line chart with compliance gate; goal-scoped.
  "volatility_chart",
  // QL-313 — equity share stacked bar with compliance gate; goal-scoped.
  "equity_share_chart",
  // QL-375 group 10 — a mandate belongs to the goal; both blocks are goal-scoped.
  "mandate",
  "mandate_comparison",
] as const;

export type GoalScopedComponentType = (typeof GOAL_SCOPED_COMPONENTS)[number];

/**
 * Valid in a global section and in a per-goal section, never in a per-portfolio
 * one — the PDF analogue of the flow policy's `GLOBAL_AND_GOAL_COMPONENTS`.
 *
 * QL-212 — a risk model may capture each of its three inputs per goal or once
 * per client, so the client-level parts of the risk block print once at the
 * top of the document and the goal-level parts under each goal. This list only
 * says WHERE the component may sit; which parts a given placement prints is
 * decided by `renderedRiskSectionsInPdfSection` in `risk-input-scope.ts`, and a
 * placement that prints nothing is refused at save time by the PDF editor's
 * write path.
 */
export const GLOBAL_AND_GOAL_SCOPED_COMPONENTS = [
  "risk_assessment",
  // QL-315 — the asset-class forecast merges portfolios, so it needs more than
  // one to say anything: under a goal it charts that goal's portfolios, under a
  // global section every portfolio in the instance. A per-portfolio placement
  // would chart a single fixed mix and is refused.
  "asset_class_forecast",
] as const;

export type GlobalAndGoalScopedComponentType = (typeof GLOBAL_AND_GOAL_SCOPED_COMPONENTS)[number];

// Portfolio-level components — schedule, holdings, and analytics moved to the portfolio (QP-150).
// These display data for a single portfolio and are only valid in per_portfolio sections.
export const PORTFOLIO_SCOPED_COMPONENTS = [
  "goal_schedule",
  "portfolio_display",
  "portfolio_xray",
  "asset_allocation",
  "historical_return",
  "portfolio_forecast",
  // QL-730 B2 — one portfolio's own mandate check.
  "mandate_portfolio",
] as const;

export type PortfolioScopedComponentType = (typeof PORTFOLIO_SCOPED_COMPONENTS)[number];

/**
 * Components with no scope of their own — allowed in EVERY section, not only in
 * a global one. (The flow policy's similarly-named `GLOBAL_ONLY_COMPONENTS`
 * means the opposite; the two files do not share a vocabulary.)
 */
export const GLOBAL_COMPONENTS = [
  "text",
  "heading",
  // QL-384 — typically on its own page after the cover, in any section.
  "table_of_contents",
  // QL-384 — the financial situation is captured once per flow.
  "financial_situation",
  "field_value",
  "divider",
  "spacer",
  "table",
  "image",
  "advisor_image",
  "page_break",
  "client_classification",
  "product_assessment",
  // QL-505 — the standalone knowledge quiz's PDF block, instance-wide like PA.
  "quiz_results",
  "trapets_lookup",
  "sumsub_verification",
  "enin_lookup",
  "cost_table",
  "cost_chart",
  "portfolio_costs",
  "product_costs",
  "expected_value",
  "wealth_plan",
  // QL-281 — the goal list spans every goal of the instance.
  "goal_list",
  // QP-179 — the client's stated preferences are instance-wide.
  "sustainability_preferences",
  // QP-224 — client preference answers are instance-wide.
  "client_preferences",
  // QP-184 — group members + related parties are client-level.
  "group_members",
  "client_parties",
] as const;

export type GlobalComponentType = (typeof GLOBAL_COMPONENTS)[number];

export function isComponentAllowedInSection(
  componentType: string,
  sectionScope: PdfSectionScope,
): boolean {
  if (GLOBAL_COMPONENTS.includes(componentType as GlobalComponentType)) {
    return true;
  }
  if (
    GLOBAL_AND_GOAL_SCOPED_COMPONENTS.includes(componentType as GlobalAndGoalScopedComponentType)
  ) {
    return sectionScope !== "per_portfolio";
  }
  if (GOAL_SCOPED_COMPONENTS.includes(componentType as GoalScopedComponentType)) {
    return sectionScope === "per_goal";
  }
  if (PORTFOLIO_SCOPED_COMPONENTS.includes(componentType as PortfolioScopedComponentType)) {
    return sectionScope === "per_portfolio";
  }
  return false;
}

export function getComponentScopeErrorMessage(
  componentType: string,
  sectionScope: PdfSectionScope,
): string {
  if (
    GLOBAL_AND_GOAL_SCOPED_COMPONENTS.includes(componentType as GlobalAndGoalScopedComponentType)
  ) {
    return `"${componentType}" components can only be used in global or per-goal sections. This component has no per-portfolio form.`;
  }
  if (GOAL_SCOPED_COMPONENTS.includes(componentType as GoalScopedComponentType)) {
    return `"${componentType}" components can only be used in per-goal sections. This component displays goal-specific data.`;
  }
  if (PORTFOLIO_SCOPED_COMPONENTS.includes(componentType as PortfolioScopedComponentType)) {
    return `"${componentType}" components can only be used in per-portfolio sections. This component displays portfolio-specific data.`;
  }
  return `"${componentType}" component is not allowed in ${sectionScope} sections.`;
}

// ============================================
// SECTION INPUT TYPES
// ============================================

// Advisors create only global or goal sections. Creating a goal section auto-creates its bonded
// per_portfolio sub-node server-side (QP-150) — `per_portfolio` is never sent from the UI here.
export type CreatePdfSectionInput = {
  name: string;
  scope: "global" | "per_goal";
  order?: number;
};

export type UpdatePdfSectionInput = {
  name?: string;
  order?: number;
  orientation?: PdfPageOrientation;
  bgColor?: string | null;
  bgImage?: string | null;
  bgImageType?: string | null;
  headerText?: string | null;
  headerTextAlign?: string | null;
  headerLogo?: string | null;
  headerLogoType?: string | null;
  headerLogoAlign?: string | null;
  headerShowLine?: boolean;
  headerLineColor?: string | null;
  footerShowNumbers?: boolean;
  footerPageNumberAlign?: PdfPageNumberAlign;
  footerShowLine?: boolean;
  footerLineColor?: string | null;
};

// ============================================
// PDF FLOW HELPER TYPES
// ============================================

export type PdfFlowField = {
  id: string;
  name: string;
  type: string;
  // Option rows with stable ids (QL-201); condition values store the id.
  options: FlowFieldOption[];
  scope: ConditionScope;
};

export type PdfFlowConfig = {
  fields: Array<{ id: string; name: string; type: string; order: number }>;
  hasRiskModel: boolean;
  hasPortfolioCollection: boolean;
};

export type PdfCompletedSession = {
  id: string;
  name: string | null;
  clientName: string;
  completedAt: string | null;
};
